import { InstanciaPaso } from "../types/instanciaPaso";

/**
 * Arma la "vista de proceso" del estudiante a partir de sus InstanciaPaso.
 *
 * El backend entrega todas las instancias (varios ciclos: cada semestre reintenta
 * con una nueva Propuesta como raíz). Acá, solo en el frontend, reconstruimos:
 *
 * - el ciclo activo (la cadena `depende_de` que termina en la punta más reciente),
 * - las acciones (los pasos abiertos de ese ciclo, el más avanzado primero),
 * - los próximos pasos del ciclo (posteriores a la acción más avanzada),
 * - el historial (pasos ya finalizados + eventos de tema), en orden de flujo,
 * - y los intentos previos (raíces de ciclos anteriores) para resumirlos.
 *
 * Los eventos de tema (elección y cambio) se derivan comparando el `tema` de cada
 * paso a lo largo del flujo; no vienen del backend.
 */

// Estados que ya no piden acción del estudiante.
const FINALIZADOS = new Set(["aprobado", "reprobado"]);

export interface EventoTema {
  tipo: "eleccion" | "cambio";
  tema: string;
  temaId: number | null;
}

export type EntradaHistorial =
  | { kind: "paso"; inst: InstanciaPaso }
  | { kind: "evento"; evento: EventoTema; ref: InstanciaPaso };

export interface AnalisisProceso {
  /** Cadena del ciclo activo, en orden de flujo (raíz → punta). */
  ciclo: InstanciaPaso[];
  /**
   * Pasos que el estudiante tiene abiertos ahora, el más avanzado primero (vacío si no
   * hay pendientes). Casi siempre es uno; son dos mientras hace la Vía Rápida, porque
   * la Propuesta Inicial sigue abierta hasta que la cierra UCampus y puede entregar en
   * cualquiera de las dos.
   */
  acciones: InstanciaPaso[];
  /** Pasos del ciclo posteriores a la acción más avanzada (aún no exigidos). */
  proximos: InstanciaPaso[];
  /** Historial en orden de flujo (raíz → punta); la vista lo invierte para mostrar lo reciente arriba. */
  historial: EntradaHistorial[];
  /** Raíces de ciclos anteriores (no el activo), de más reciente a más antiguo. */
  intentosPrevios: InstanciaPaso[];
}

// Compara [ano, semestre, id] de mayor a menor (más reciente primero).
function masReciente(a: InstanciaPaso, b: InstanciaPaso): number {
  return b.ano - a.ano || b.semestre - a.semestre || b.id - a.id;
}

export function analizarProceso(instancias: InstanciaPaso[]): AnalisisProceso | null {
  if (!instancias.length) return null;

  const porId = new Map<number, InstanciaPaso>();
  instancias.forEach((i) => porId.set(i.id, i));

  // Puntas: instancias que nadie referencia como `depende_de`. Cada una cierra una
  // cadena; la que cuenta ancla el ciclo activo.
  const referenciadas = new Set<number>();
  instancias.forEach((i) => i.depende_de != null && referenciadas.add(i.depende_de));

  // Cadena raíz → punta siguiendo `depende_de` hacia atrás.
  const cadenaDe = (punta: InstanciaPaso): InstanciaPaso[] => {
    const cadena: InstanciaPaso[] = [];
    const visto = new Set<number>();
    let cur: InstanciaPaso | undefined = punta;
    while (cur && !visto.has(cur.id)) {
      cadena.unshift(cur);
      visto.add(cur.id);
      cur = cur.depende_de != null ? porId.get(cur.depende_de) : undefined;
    }
    return cadena;
  };

  // El ciclo activo es el de la punta que cuenta, con la misma regla que usa el backend
  // para la lista de estudiantes (`_la_que_cuenta`): el semestre más reciente; dentro
  // de él, lo que sigue abierto antes que lo cerrado; después el más avanzado en el
  // flujo (la cadena más larga) y recién al final el id. Antes decidía solo el id, y una
  // instancia suelta creada después —una Propuesta Inicial que llegó por inscripción
  // al curso cuando el estudiante ya estaba en el Informe por la Vía Rápida— se quedaba
  // con el ciclo y dejaba el Informe fuera de la vista.
  const cadenas = instancias.filter((i) => !referenciadas.has(i.id)).map(cadenaDe);
  const abierta = (c: InstanciaPaso[]) => c.some((i) => !FINALIZADOS.has(i.estado));
  cadenas.sort((a, b) => {
    const pa = a[a.length - 1];
    const pb = b[b.length - 1];
    return (
      pb.ano - pa.ano ||
      pb.semestre - pa.semestre ||
      Number(abierta(b)) - Number(abierta(a)) ||
      b.length - a.length ||
      masReciente(pa, pb)
    );
  });
  const ciclo = cadenas[0];
  const raizActiva = ciclo[0];

  // Acciones = todos los pasos abiertos del ciclo, el más avanzado primero. Lo que
  // viene después del más avanzado son los próximos.
  const abiertos = ciclo.map((i, k) => [i, k] as const).filter(([i]) => !FINALIZADOS.has(i.estado));
  const acciones = abiertos.map(([i]) => i).reverse();
  const idxAccion = abiertos.length ? abiertos[abiertos.length - 1][1] : -1;
  const proximos = idxAccion >= 0 ? ciclo.slice(idxAccion + 1) : [];

  // Eventos de tema: primera aparición (elección) y cambios posteriores.
  const eventoEnIdx = new Map<number, EventoTema>();
  let temaPrev: string | null = null;
  ciclo.forEach((i, k) => {
    if (i.tema && i.tema !== temaPrev) {
      eventoEnIdx.set(k, { tipo: temaPrev === null ? "eleccion" : "cambio", tema: i.tema, temaId: i.tema_id });
      temaPrev = i.tema;
    }
  });

  // Historial (orden de flujo): eventos de tema + pasos finalizados anteriores a la
  // acción más avanzada.
  const historial: EntradaHistorial[] = [];
  ciclo.forEach((inst, k) => {
    const evento = eventoEnIdx.get(k);
    if (evento) historial.push({ kind: "evento", evento, ref: inst });
    const antesDeAccion = idxAccion < 0 || k < idxAccion;
    if (FINALIZADOS.has(inst.estado) && antesDeAccion) historial.push({ kind: "paso", inst });
  });

  // Intentos previos: raíces (sin dependencia) que no son la raíz del ciclo activo.
  const intentosPrevios = instancias.filter((i) => i.depende_de == null && i.id !== raizActiva.id).sort(masReciente);

  return { ciclo, acciones, proximos, historial, intentosPrevios };
}
